// SetupChecklist — card colapsável no topo do Dashboard que mostra progresso
// de configuração inicial do Control Center. Some quando 100% completo.
// Wesley pode minimizar manualmente (persiste em localStorage).

import { useEffect, useState } from 'react';
import { CheckCircle2, ChevronDown, ChevronUp, Circle, ExternalLink, Rocket } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { cn } from '@control-center/ui';

import { useSetupChecklist } from '../../hooks/use-setup-checklist';
import type { ChecklistItem } from '../../lib/setup-api';

const STORAGE_KEY = 'cc:setup-checklist-collapsed';

export function SetupChecklist(): JSX.Element | null {
  const { data, isLoading, isError } = useSetupChecklist();
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(STORAGE_KEY) === 'true';
  });

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, collapsed ? 'true' : 'false');
  }, [collapsed]);

  if (isLoading || isError || !data) return null;
  // Some quando tudo configurado
  if (data.percent === 100) return null;

  const integrations = data.items.filter((i) => i.category === 'integrations');
  const dataItems = data.items.filter((i) => i.category === 'data');

  return (
    <section className="rounded-[14px] border border-line bg-panel">
      <header
        role="button"
        tabIndex={0}
        onClick={() => setCollapsed((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setCollapsed((v) => !v);
          }
        }}
        className="flex cursor-pointer items-center gap-3 px-5 py-4"
      >
        <span className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-brand-soft text-brand">
          <Rocket size={15} strokeWidth={1.8} />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-[14px] font-semibold tracking-[-0.005em] text-ink">
            Configuração inicial
          </h2>
          <p className="text-[12px] text-muted">
            {data.done} de {data.total} passos concluídos · Termine pra deixar o Control Center
            operando sozinho.
          </p>
        </div>
        <ProgressRing percent={data.percent} />
        <button
          type="button"
          aria-label={collapsed ? 'Expandir' : 'Recolher'}
          className="flex h-8 w-8 items-center justify-center rounded-[6px] text-muted hover:bg-panel-2 hover:text-ink"
        >
          {collapsed ? (
            <ChevronDown size={15} strokeWidth={2} />
          ) : (
            <ChevronUp size={15} strokeWidth={2} />
          )}
        </button>
      </header>

      {!collapsed && (
        <div className="border-t border-line/60 px-5 py-4">
          <Group title="Integrações" items={integrations} />
          <div className="mt-5">
            <Group title="Primeiros dados" items={dataItems} />
          </div>
        </div>
      )}
    </section>
  );
}

function Group({ title, items }: { title: string; items: ChecklistItem[] }): JSX.Element {
  return (
    <div>
      <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-3">
        {title}
      </h3>
      <ul className="divide-y divide-line/40">
        {items.map((item) => (
          <ChecklistRow key={item.id} item={item} />
        ))}
      </ul>
    </div>
  );
}

function ChecklistRow({ item }: { item: ChecklistItem }): JSX.Element {
  const navigate = useNavigate();
  const Icon = item.done ? CheckCircle2 : Circle;

  return (
    <li className="flex items-start gap-3 py-3">
      <span
        className={cn(
          'mt-[2px] flex h-5 w-5 shrink-0 items-center justify-center',
          item.done ? 'text-good' : 'text-muted-3',
        )}
        aria-hidden
      >
        <Icon size={16} strokeWidth={item.done ? 2 : 1.5} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-3">
          <span
            className={cn(
              'text-[13px] font-medium',
              item.done ? 'text-muted line-through' : 'text-ink',
            )}
          >
            {item.label}
          </span>
          {!item.done && (
            <button
              type="button"
              onClick={() => navigate(item.href)}
              className="inline-flex items-center gap-1 text-[12px] font-medium text-info hover:underline"
            >
              Configurar
              <ExternalLink size={11} strokeWidth={2} />
            </button>
          )}
        </div>
        <p className="mt-[2px] text-[12px] text-muted">{item.description}</p>
        {!item.done && item.manualHint && (
          <p className="mt-1 text-[11px] text-muted-2">{item.manualHint}</p>
        )}
      </div>
    </li>
  );
}

function ProgressRing({ percent }: { percent: number }): JSX.Element {
  const size = 36;
  const stroke = 3;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percent / 100) * circumference;

  return (
    <div className="relative flex h-9 w-9 shrink-0 items-center justify-center">
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="currentColor"
          strokeWidth={stroke}
          fill="none"
          className="text-line"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="currentColor"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          fill="none"
          className="text-brand transition-[stroke-dashoffset] duration-500"
        />
      </svg>
      <span className="absolute text-[10px] font-semibold text-ink tnum">{percent}%</span>
    </div>
  );
}
